<template>
  <widget-submissions-layout
    :page_title="`${organization_label}: Order Confirmations`"
    heading="Order Confirmation Entries"
    description="View and manage order confirmation entries. To begin generating order confirmations, you must first configure an order confirmation form and the 'Order Placed: Request Confirmation and Opt-In (Uses Order Confirmation Form)' automated message."
    search_label="Search by order #, name, email, or phone"
    :search_text.sync="full_text"
    :loading="loading"
    @refresh="() => loadSubmissions()"
  >
    <template #menu-items>
      <v-subheader>EXPORT</v-subheader>
      <v-list-item @click="exportSubmissions">
        <v-list-item-icon><v-icon>mdi-download</v-icon></v-list-item-icon>
        <v-list-item-title>Export Entries</v-list-item-title>
      </v-list-item>

      <v-divider />

      <v-subheader>ANALYTICS & FORMS</v-subheader>
      <v-list-item :to="{
        name: 'search-forms-order-confirmation',
        params: { organization_id: $route.params.organization_id },
      }">
        <v-list-item-icon><v-icon>mdi-form-select</v-icon></v-list-item-icon>
        <v-list-item-title>All Order Confirmation Forms</v-list-item-title>
      </v-list-item>
      <v-list-item :to="{
        name: 'order-confirmation-analytics',
        params: { organization_id: $route.params.organization_id },
      }">
        <v-list-item-icon><v-icon>mdi-chart-line</v-icon></v-list-item-icon>
        <v-list-item-title>All Order Confirmation Analytics</v-list-item-title>
      </v-list-item>

      <template v-if="order_conf_auto_message_label && order_conf_auto_message_link">
        <v-divider />

        <v-subheader>AUTOMATED MESSAGES</v-subheader>

        <v-list-item @click="viewAllAutomatedMessages">
          <v-list-item-icon><v-icon>mdi-forum</v-icon></v-list-item-icon>
          <v-list-item-title>{{ order_conf_auto_message_label }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="should_enable_generate_submissions">
        <v-divider />
        <v-subheader>STAFF ACTIONS</v-subheader>
        <v-list-item @click="showGenerateSubmissionsDialog">
          <v-list-item-icon><v-icon>mdi-plus-circle-multiple</v-icon></v-list-item-icon>
          <v-list-item-title>Generate Test Entries</v-list-item-title>
        </v-list-item>
      </template>
    </template>

    <template #filters>
      <v-select
        v-model="selected_form_id"
        :items="form_options"
        label="Filter by Form"
        item-text="text"
        item-value="value"
        clearable
        hide-details
        dense
        outlined
        style="min-width: 200px;"
        @input="() => loadSubmissions(true)"
      />

      <v-checkbox
        v-model="show_test_submissions"
        label="Show Test Entries"
        @change="() => loadSubmissions(true)"
        hide-details
        dense
        class="mt-0"
      />
    </template>

    <template #filter-actions>
      <v-btn-toggle
        v-model="filter_state"
        dense
        mandatory
      >
        <v-btn value="all">All</v-btn>
        <v-btn value="pending">Pending</v-btn>
        <v-btn value="finished-confirmed">Confirmed</v-btn>
        <v-btn value="finished-update-order">Update Requested</v-btn>
      </v-btn-toggle>
    </template>

    <template #table>
      <v-data-table
        :headers="headers"
        :items="results"
        :loading="loading"
        :server-items-length="pagination.total_items"
        :items-per-page="pagination.itemsPerPage"
        :page="pagination.page"
        disable-sort
        @update:options="updatePagination"
        :footer-props="{
          itemsPerPageText: 'Entries per page:',
          itemsPerPageOptions: [25, 50, 100],
        }"
        item-key="confirmation_id"
        class="enhanced-submissions-table elevation-1"
        :multi-sort="false"
      >
        <template #item.state="{ item }">
          <v-chip
            v-if="item.state"
            small
            :color="getOrderStateColor(item.state)"
            dark
            class="status-chip"
          >
            {{ getOrderStateText(item.state) }}
          </v-chip>
          <span
            v-else
            class="text--secondary"
          >N/A</span>
        </template>

        <template #item.customer_name="{ item }">
          {{ item.first_name }} {{ item.last_name }}
        </template>

        <template #item.endpoint="{ item }">
          <span v-if="item.endpoint">{{ $rc.pretty(item.endpoint, 'endpoint_no_parens') }}</span>
          <span
            v-else
            class="text--secondary"
          >N/A</span>
        </template>

        <template #item.email_consent="{ item }">
          <div v-if="item.state === 'pending'" class="text--secondary text-caption">
            -
          </div>
          <v-icon
            v-else
            :color="item.email_consent_agreed ? 'green' : 'grey'"
            small
          >
            {{ item.email_consent_agreed ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
        </template>

        <template #item.phone_consent="{ item }">
          <div v-if="item.state === 'pending'" class="text--secondary text-caption">
            -
          </div>
          <v-icon
            v-else
            :color="item.phone_consent_agreed ? 'green' : 'grey'"
            small
          >
            {{ item.phone_consent_agreed ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
        </template>

        <template #item.time_create_datetime="{ item }">
          <div>{{ $rc.pretty(item.time_create, 'date') }}</div>
          <div class="text-caption text--secondary">{{ $rc.pretty(item.time_create, 'time') }}</div>
        </template>

        <template #item.time_submit_datetime="{ item }">
          <div v-if="item.time_submit">
            <div>{{ $rc.pretty(item.time_submit, 'date') }}</div>
            <div class="text-caption text--secondary">{{ $rc.pretty(item.time_submit, 'time') }}</div>
          </div>
          <span
            v-else
            class="text--secondary"
          >N/A</span>
        </template>

        <template #item.actions="{ item }">
          <v-menu
            bottom
            left
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="viewDetails(item)">
                <v-list-item-icon>
                  <v-icon>mdi-information-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>View Details...</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </template>

    <template #dialogs>
      <v-dialog
        v-model="show_details_dialog"
        max-width="900px"
        scrollable
      >
        <v-card v-if="selected_item">
          <v-card-title class="d-flex align-center">
            <span class="text-h5">Order Confirmation Details</span>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="show_details_dialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-6">
            <v-row
              v-if="selected_item.requested_changes && selected_item.change_request_reason"
              class="mb-3"
            >
              <v-col cols="12">
                <div class="text-caption text--secondary mb-1">Order Update Request Reason</div>
                <div class="reason-block">
                  {{ selected_item.change_request_reason }}
                </div>
                <div
                  v-if="selected_item.update_order_inbox_id"
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    color="primary"
                    :to="{
                      name: 'search-inbox-contact-requests',
                      params: {
                        organization_id: $route.params.organization_id,
                        inbox_id: selected_item.update_order_inbox_id
                      }
                    }"
                    target="_blank"
                  >
                    <v-icon
                      left
                      small
                    >mdi-inbox-arrow-down</v-icon>
                    View in Inbox
                  </v-btn>
                </div>
              </v-col>

            </v-row>

            <v-divider
              v-if="selected_item.requested_changes && selected_item.change_request_reason"
              class="mb-4"
            />

            <v-row>
              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Contact</div>
                <div class="d-flex align-center mb-1">
                  <v-icon
                    small
                    left
                  >mdi-account-outline</v-icon>
                  <span>{{ selected_item.first_name }} {{ selected_item.last_name }}</span>
                </div>
                <div
                  v-if="selected_item.email_address"
                  class="d-flex align-center mb-1"
                >
                  <v-icon
                    small
                    left
                  >mdi-email-outline</v-icon>
                  <a :href="'mailto:' + selected_item.email_address">{{ selected_item.email_address }}</a>
                </div>
                <div
                  v-if="selected_item.endpoint"
                  class="d-flex align-center"
                >
                  <v-icon
                    small
                    left
                  >mdi-phone-outline</v-icon>
                  <span>{{ $rc.pretty(selected_item.endpoint, 'endpoint_no_parens') }}</span>
                </div>
                <div
                  v-if="!selected_item.email_address && !selected_item.endpoint"
                  class="text--secondary pl-7"
                >
                  No contact info provided.
                </div>
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Ship To Address</div>
                <div
                  v-if="selected_item.ship_to_address"
                  class="address-block"
                  v-html="selected_item.ship_to_address"
                >
                </div>
                <span
                  v-else
                  class="text--secondary"
                >N/A</span>
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Bill To Address</div>
                <div
                  v-if="selected_item.bill_to_address"
                  class="address-block"
                  v-html="selected_item.bill_to_address"
                >
                </div>
                <span
                  v-else
                  class="text--secondary"
                >N/A</span>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <v-row>
              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Order Confirmation Details</div>
                <div class="mb-1">
                  <strong>Order #:</strong>
                  <span class="ml-1">{{ selected_item.order_number || 'N/A' }}</span>
                </div>
                <div class="mb-1">
                  <strong>Link:</strong>
                  <v-btn
                    text
                    small
                    color="primary"
                    @click="viewConfirmation(selected_item)"
                    class="pa-0 ml-1"
                    style="min-width: auto; height: auto; top: -1px;"
                  >
                    {{ selected_item.short_id }}
                  </v-btn>
                </div>
                <div class="mb-1">
                  <strong>Last Viewed:</strong>
                  <span
                    v-if="selected_item.last_viewed_time"
                  >
                    {{ $rc.pretty(selected_item.last_viewed_time, 'date') }} at {{ $rc.pretty(selected_item.last_viewed_time, 'time') }}
                  </span>
                  <span
                    v-else
                    class="text--secondary ml-1"
                  >Not Viewed</span>
                </div>
                <div class="d-flex align-center">
                  <strong class="mr-1">Type:</strong>
                  <v-chip
                    small
                    :color="selected_item.is_test_submission ? 'orange' : 'green'"
                    :text-color="selected_item.is_test_submission ? 'white' : 'white'"
                    class="status-chip"
                    style="height: 20px; font-size: 0.7rem;"
                  >
                    {{ selected_item.is_test_submission ? 'Test' : 'Live' }}
                  </v-chip>
                </div>
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Form Consent</div>
                <div
                  v-if="selected_item.state === 'pending'"
                  class="d-flex align-center text--secondary"
                >
                  <v-icon
                    small
                    left
                    color="grey"
                  >mdi-information-outline</v-icon>
                  <span style="font-size: 0.875rem;">Will be captured on submission.</span>
                </div>
                <div v-else>
                  <div>
                    <v-icon
                      :color="selected_item.email_consent_agreed ? 'green' : 'grey'"
                      small
                      left
                    >
                      {{ selected_item.email_consent_agreed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                    Email Consent
                  </div>
                  <div>
                    <v-icon
                      :color="selected_item.phone_consent_agreed ? 'green' : 'grey'"
                      small
                      left
                    >
                      {{ selected_item.phone_consent_agreed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                    Phone Consent
                  </div>
                </div>
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <div class="text-caption text--secondary mb-1">Device Details on Submit</div>
                <div
                  v-if="selected_item.state === 'pending'"
                  class="d-flex align-center text--secondary"
                >
                  <v-icon
                    small
                    left
                    color="grey"
                  >mdi-information-outline</v-icon>
                  <span style="font-size: 0.875rem;">Will be captured on submission.</span>
                </div>
                <div
                  v-if="selected_item.state !== 'pending'"
                >
                  <div
                    class="mb-1"
                  >
                    <strong>IP Address:</strong>
                    <span> {{ selected_item.ip_address_on_submit || 'Unknown' }}</span>
                  </div>
                  <div
                    class="mb-1"
                  >
                    <strong class="mr-1">User Agent:</strong>
                    <span class="text-caption">{{ selected_item.user_agent_on_submit || 'Unknown' }}</span>
                  </div>
                </div>
              </v-col>

            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="show_view_warning_dialog"
        max-width="600px"
      >
        <v-card>
          <v-card-title class="text-h5">View Confirmation Form?</v-card-title>
          
          <v-card-text>
            <p class="mb-0">
              Viewing this order confirmation form will update its "Last Viewed" time. Are you sure you want
              to proceed?
            </p>
            
            <template v-if="submission_to_view && submission_to_view.state === 'pending'">
              <v-alert
                color="primary"
                class="my-3"
                type="info"
                outlined
                dense
              >
                <strong>Important:</strong> Do not submit this form on behalf of your customer. Order confirmations must be completed by the customer themselves.
              </v-alert>

              <v-checkbox
                v-model="acknowledge_no_submit"
                class="mt-2"
                hide-details
              >
                <template #label>
                  <span class="text-body-2">I understand I should not submit this form on behalf of my customer</span>
                </template>
              </v-checkbox>
            </template>
          </v-card-text>

          <v-card-actions>
            <v-spacer />

            <v-btn
              text
              @click="closeViewWarningDialog"
            >Cancel</v-btn>
            <v-btn
              color="primary"
              text
              :disabled="submission_to_view && submission_to_view.state === 'pending' && !acknowledge_no_submit"
              @click="confirmViewConfirmation"
            >View</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <v-dialog
        v-model="generate_submissions_dialog"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">Generate Test Entries</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="selected_forms_for_generation"
                    :items="selectable_forms_for_generate_submissions"
                    label="Published Order Confirmation Form"
                    item-text="text"
                    item-value="value"
                    outlined
                    multiple
                    chips
                    deletable-chips
                    :rules="[rules.required]"
                    hint="Select one or more forms to generate entries for"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="submission_count"
                    label="Number of Entries"
                    type="number"
                    min="1"
                    max="50"
                    outlined
                    dense
                    :rules="[rules.required, rules.submission_count_range]"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="seed"
                    label="Seed (optional)"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hint="Use a seed to get reproducible test data"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-container>
            <small class="text--secondary">
              This will generate test order confirmation entries with varying data, opt-in states, and double opt-in
              statuses. All entries will use test phone numbers and emails. When multiple settings
              are selected, entries will be distributed evenly across them.
            </small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="generate_submissions_dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="generateTestSubmissions"
              :loading="generating_submissions"
              :disabled="!selected_forms_for_generation.length || !submission_count"
            >
              Generate
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </widget-submissions-layout>
</template>

<script>
import _ from 'lodash';
import WidgetSubmissionsLayout from '@/components/widget/widget-submissions-layout.vue';

export default {
  name: 'OrderConfirmationSubmissions',

  components: {
    WidgetSubmissionsLayout,
  },

  data() {
    return {
      loading: false,
      exporting: false,
      results: [],
      pagination: {
        page: 1,
        itemsPerPage: 25,
        total_items: 0,
        sortBy: ['time_submit'],
        sortDesc: [true],
      },
      full_text: null,
      filter_state: 'all',
      selected_form_id: null,
      show_test_submissions: false,
      forms: [],

      show_details_dialog: false,
      selected_item: null,

      show_view_warning_dialog: false,
      submission_to_view: null,
      acknowledge_no_submit: false,

      generate_submissions_dialog: false,
      generating_submissions: false,
      selected_forms_for_generation: [],
      submission_count: 10,
      seed: null,

      order_conf_auto_message_link: null,
      order_conf_auto_message_label: '',

      rules: {
        required: value => !!value || 'Required.',
        submission_count_range: value =>
          (value >= 1 && value <= 500) || 'Must be between 1 and 500.',
      },
    };
  },

  computed: {
    headers() {
      return [
        {
          text: 'State',
          value: 'state',
          align: 'center',
          sortable: true,
        },
        { text: 'Order #', value: 'order_number', sortable: true },
        { text: 'Form Name', value: 'settings_name', sortable: true },
        {
          text: 'Customer',
          value: 'customer_name',
          sortable: true,
          sortValue: 'last_name',
        },
        { text: 'Phone', value: 'endpoint', sortable: true },
        {
          text: 'Email Consent',
          value: 'email_consent',
          width: '140px',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Phone Consent',
          value: 'phone_consent',
          width: '140px',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Created',
          value: 'time_create_datetime',
          sortable: true,
          sortValue: 'time_create',
        },
        {
          text: 'Submitted',
          value: 'time_submit_datetime',
          sortable: true,
          sortValue: 'time_submit',
        },
        { text: '', value: 'actions', align: 'center', sortable: false, width: '50px' },
      ];
    },

    form_options() {
      if (!this.forms) return [];
      return this.forms.map(form => ({
        text: form.internal_label || 'Untitled Form',
        value: form.order_confirmation_settings_id,
      }));
    },

    no_data_message() {
      if (this.full_text && this.full_text.trim()) {
        return `No entries found matching "${this.full_text}"`;
      }
      return 'No entries available';
    },

    organization_label() {
      return this.$rc.$state.getOrganizationLabel();
    },

    should_enable_generate_submissions() {
      return (this.$rc.inDev() || this.$rc.inStaging()) && this.$rc.$state.isAuthActorSystemAdmin();
    },
    
    selectable_forms_for_generate_submissions() {
      if (!this.forms) return [];
      return this.forms.filter(form => form.is_published).map(form => ({
        text: form.internal_label || 'Untitled Form',
        value: form.order_confirmation_settings_id,
      }));

    },
  },

  watch: {
    filter_state() {
      this.loadSubmissions(true);
    },
    full_text() {
      this.update();
    },
  },

  mounted() {
    if (this.$route.query.order_confirmation_settings_id) {
      this.selected_form_id = this.$route.query.order_confirmation_settings_id;
    }
    this.loadSubmissions();
    this.loadForms();
    this.getOrderConfAutomatedMessage();
  },

  methods: {
    async loadSubmissions(reset_to_first_page = false) {
      this.loading = true;

      let sort_by_field = this.pagination.sortBy.length ? this.pagination.sortBy[0] : 'time_submit';
      const sort_header = this.headers.find(h => h.value === sort_by_field);
      if (sort_header && sort_header.sortValue) {
        sort_by_field = sort_header.sortValue;
      }

      const params = {
        organization_id: this.$route.params.organization_id,
        page: this.pagination.page,
        items_per_page: this.pagination.itemsPerPage,

        full_text: this.full_text,
        state_filter: this.filter_state === 'all' ? null : this.filter_state,
        order_confirmation_settings_id: this.selected_form_id,
        include_test_submissions: this.show_test_submissions,

        sort_by: sort_by_field,
        sort_desc: this.pagination.sortDesc.length ? this.pagination.sortDesc[0] : true,
      };

      Object.keys(params).forEach(key => params[key] == null && delete params[key]);

      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/order-confirmation/submissions/search',
        params: params,
        on_success: data => {
          this.results = data.results || [];
          this.pagination.total_items = data.count ?? data.total_count ?? 0;

          if (reset_to_first_page) {
            this.pagination.page = 1;
          }

          this.loading = false;
        },
        on_error: error => {
          this.$rc.showError('Failed to load order confirmation entries', error);
          this.results = [];
          this.pagination.total_items = 0;
          this.loading = false;
        },
      });
    },

    getOrderConfAutomatedMessage() {
      this.$rc.rest({
          spinner: false,
          endpoint: 'PUT: /api/v1/auto-messages/org-profile',

          params : {
            organization_id: this.$route.params.organization_id,
          },

          on_success: (data) => {
            const order_conf_auto_message = data.templates.find(({ template_context }) => template_context === 'order-placed-request-order-confirmation');
            if (order_conf_auto_message) {
              this.order_conf_auto_message_link = `/app/auto-messages/${this.$route.params.organization_id}/order-placed-request-order-confirmation/00000000-0000-0000-0000-000000000000`;
              this.order_conf_auto_message_label = order_conf_auto_message.template_label;
            }
          },
          on_error: 'Unable to list automated messages',
      });
    },

    update: _.debounce(function () {
      this.loadSubmissions(true);
    }, 600),

    updatePagination(options) {
      this.pagination.page = options.page;
      this.pagination.itemsPerPage = options.itemsPerPage;
      this.pagination.sortBy = options.sortBy;
      this.pagination.sortDesc = options.sortDesc;
      this.loadSubmissions();
    },

    viewDetails(item) {
      this.selected_item = item;
      this.show_details_dialog = true;
    },

    viewConfirmation(item) {
      this.submission_to_view = item;
      this.show_view_warning_dialog = true;
    },

    confirmViewConfirmation() {
      if (!this.submission_to_view) return;

      const url_path = `/app/oc/${this.submission_to_view.short_id}`;

      window.open(url_path, '_blank');

      this.closeViewWarningDialog();

      setTimeout(() => {
        this.loadSubmissions();
      }, 1500);
    },

    closeViewWarningDialog() {
      this.show_view_warning_dialog = false;
      this.submission_to_view = null;
      this.acknowledge_no_submit = false;
    },

    exportSubmissions() {
      const params = {
        organization_id: this.$route.params.organization_id,
        full_text: this.full_text,
        state_filter: this.filter_state === 'all' ? null : this.filter_state,
        order_confirmation_settings_id: this.selected_form_id,
      };

      this.$rc.excelExport('EXPORT: /api/v1/order-confirmation/submissions', params, () => {
      });
    },

    viewAllAnalytics() {
      this.$router.push({
        name: 'order-confirmation-analytics',
        params: { organization_id: this.$route.params.organization_id },
      });
    },

    loadForms() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/order-confirmation/settings/search',
        params: {
          organization_id: this.$route.params.organization_id,
        },
        on_success: data => {
          this.forms = data.results || [];
        },
        on_error: 'Unable to load order confirmation forms',
      });
    },

    getOrderStateColor(state) {
      switch (state) {
        case 'pending':
          return 'orange';
        case 'finished-confirmed':
          return 'green';
        case 'finished-update-order':
          return 'blue';
        default:
          return 'grey';
      }
    },

    getOrderStateText(state) {
      const map = {
        pending: 'Pending',
        'finished-confirmed': 'Confirmed',
        'finished-update-order': 'Update Requested',
      };
      return map[state] || state;
    },

    viewAllAutomatedMessages() {
      this.$router.push(
        this.order_conf_auto_message_link
      );
    },

    showGenerateSubmissionsDialog() {
      this.generate_submissions_dialog = true;
    },

    generateTestSubmissions() {
      if (!this.selected_forms_for_generation.length || !this.submission_count) {
        return;
      }

      this.generating_submissions = true;

      const gen_params = {
        organization_id: this.$route.params.organization_id,
        order_confirmation_settings_ids: this.selected_forms_for_generation,
        submission_count: this.submission_count,
      };
      if (this.seed !== null && this.seed !== undefined && this.seed !== '') {
        gen_params.seed = this.seed;
      }

      this.$rc.rest({
        spinner: true,
        endpoint: 'PUT: /api/v1/order-confirmation/generate-test-submissions',
        params: gen_params,
        on_success: data => {
          this.generating_submissions = false;
          this.generate_submissions_dialog = false;
          this.loadSubmissions();
          this.$rc.success(`Generated ${data.submissions_created} test entries successfully`);
        },
        on_error: error => {
          this.generating_submissions = false;
          this.$rc.error('Unable to generate test entries: ' + error);
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.address-block {
  white-space: pre-line;
  line-height: 1.4;
  font-size: 0.875rem;
}

.reason-block {
  white-space: pre-line;
  line-height: 1.4;
  font-size: 0.875rem;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>